import {
  AloftDate,
  HELP_CENTER_URL,
  isUndefined,
  schFilledAddress,
  schMainPropertyType,
} from '@aloft/toolkit-shared';
import {
  Block,
  Checkbox,
  Flex,
  Grid,
  Icon,
  Input,
  Link,
  Modal,
  RadioGroup,
  Select,
  SimpleInput,
  Typography,
} from '@aloft/toolkit-sui';

import { useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { AddressInput } from '../../../../components/AddressInput/AddressInput.tsx';
import { AddressInputErrors } from '../../../../components/AddressInput/AddressInputErrors.tsx';
import { FileUploader } from '../../../../components/FileUploader.tsx';
import { LoadingScreenSamFull } from '../../../../components/LoadingScreen/LoadingScreenSamFull.tsx';
import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { useZodForm } from '../../../../hooks/useZodForm.ts';
import { featuresSelectors } from '../../../../redux/features/selectors/featuresSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { parseCraftsmanUpload } from '../../utils/parseCraftsmanUpload.ts';
import { VALID_FORMS } from '../../utils/xmlExtraction/consts.ts';
import { useCreateCraftsmanOrder } from './hooks/useCreateCraftsmanOrder.ts';
import { schCraftsmanCreateOrderForm } from './types.ts';

const creationLoadingMessages = [
  { breakpoint: 0.04, message: 'Fetching property data from multiple sources' },
  { breakpoint: 0.22, message: 'Analyzing quality & condition' },
  { breakpoint: 0.41, message: 'Calculating replacement cost' },
  { breakpoint: 0.64, message: 'Subtracting depreciation' },
  { breakpoint: 0.74, message: 'Extracting site values' },
  { breakpoint: 0.9, message: 'Calculating adjustments and cost approach' },
];

export const CreateCraftsmanOrderModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { selectShouldShowCreateInspectifyOrderModal } = featuresSelectors;
  const showInspectifyOrderModal = useAppSelector(selectShouldShowCreateInspectifyOrderModal);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToaster();
  const navigate = useNavigate();
  const createCraftsmanOrder = useCreateCraftsmanOrder();
  const form = useZodForm({
    schema: schCraftsmanCreateOrderForm,
    defaultValues: {
      createBy: 'XML',
      address: {
        AddressLineText: '',
        unitNumber: '',
        CityName: '',
        StateCode: '',
        PostalCode: '',
      },
      effectiveDate: AloftDate.today(),
      propertyType: 'singleFamilyResidence',
      appraisalType: 'Desktop Appraisal',
      inspectifyOrder: false,
      options: { costToCure: true, arv: false },
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const onFormSubmit = form.handleSubmit(
    async (payload) => {
      setIsLoading(true);
      try {
        const orderID = await createCraftsmanOrder(payload);
        form.reset();
        const navPath = payload.inspectifyOrder
          ? `/order/${orderID}/paired-cost/details`
          : `/order/${orderID}/paired-cost/analysis`;
        navigate(navPath);
      } catch (e: unknown) {
        console.error(e, 'Failed to create order');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        form.trigger();
      } finally {
        setIsLoading(false);
      }
    },
    (errPayload) => console.warn(errPayload),
  );

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const ACCEPTED_FORMS = VALID_FORMS;
  const CRAFTSMAN_PROPERTY_TYPE_OPTIONS = [
    {
      label: 'Single Family Residence',
      value: schMainPropertyType.Values.singleFamilyResidence,
    },
    {
      label: 'Multi-Family Residence',
      value: schMainPropertyType.Values.multiFamily,
    },
    {
      label: 'Manufactured Home',
      value: schMainPropertyType.Values.manufacturedHome,
    },
  ];
  const APPRAISAL_TYPE_OPTIONS = [
    { label: 'Hybrid Appraisal', value: 'Hybrid Appraisal' },
    { label: 'Desktop Appraisal', value: 'Desktop Appraisal' },
  ];
  const INSPECTOR_TYPE_OPTIONS = [
    { label: 'Inspector', value: 'Inspector' },
    { label: 'Appraiser', value: 'Appraiser' },
    { label: 'Homeowner', value: 'Homeowner' },
  ];
  const createBy = form.watch('createBy');
  const inspectifyOrder = form.watch('inspectifyOrder');
  const appraisalType = form.watch('appraisalType');
  const file = form.watch('imported.xml');
  const { errors } = form.formState;
  const isFormUnfurled = file !== undefined || createBy === 'Address';
  const files = file === undefined ? [] : [{ name: file.name, id: file.name, blobID: undefined }];
  const onFileUpload = (fileToUpload: File, content: string) => {
    try {
      const { subject, comps, effectiveDate, formType } = parseCraftsmanUpload({
        upload: content,
      });

      if (!ACCEPTED_FORMS.includes(formType)) {
        form.setError('propertyType', {
          message: `${formType} is not supported in Toolkit 2.0 yet.`,
        });
        return;
      }

      form.setValue('imported.xml', fileToUpload);
      form.setValue('propertyType', subject.propertyType ?? 'singleFamilyResidence');

      form.setValue('imported.subject', subject);
      const filledAddress = schFilledAddress.parse(subject.address);

      form.setValue('address', filledAddress);

      form.setValue('imported.comps', comps);

      if (effectiveDate) {
        form.setValue('effectiveDate', effectiveDate);
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      form.trigger();
    } catch (e) {
      console.error(e);
      toast({
        type: 'error',
        id: 'add-comps-via-import',
        title: 'Failed to import properties',
        body: `Please check to make sure your XML contains a report, subject property and at least one comparable.
                Please also make sure the Toolkit order's property type matches the form type of the XML.`,
      });
    }
  };

  const resetForm = () => form.reset();

  return (
    <>
      <LoadingScreenSamFull
        isLoading={isLoading}
        what="Order"
        wrapperProps={{ backgroundColor: 'surface1' }}
        longLoaderProps={{
          estimatedDurationSeconds: createBy === 'XML' ? 16 : 2,
          messageBreakpoints: creationLoadingMessages,
        }}
      />
      <Modal
        title="Order Details"
        size="xl"
        isOpen={isOpen}
        onClose={onClose}
        primaryAction={{
          text: 'Create Order',
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick: onFormSubmit,
          disabled: !form.formState.isValid || form.formState.isSubmitting,
        }}
        secondaryAction={{
          text: 'Cancel',
          onClick: handleClose,
        }}
      >
        <FormProvider {...form}>
          <Flex flexDirection="column" gap={4.5}>
            <Flex flexDirection="column" gap={3} marginBottom={5}>
              <Typography variant="h3">How do you want to create your order?</Typography>
              <Controller
                name="createBy"
                control={form.control}
                defaultValue="XML"
                render={({ field }) => (
                  <RadioGroup
                    size="lg"
                    isRequired
                    gap=".5rem"
                    value={field.value}
                    onChange={(e) => {
                      if (e !== 'XML' && e !== 'Address') return;

                      field.onChange(e);
                    }}
                    items={[
                      { value: 'XML', label: 'Appraisal XML' },
                      { value: 'Address', label: 'Subject property address' },
                    ]}
                    __dangerDoNotUseSx={{ marginY: '1rem' }}
                  />
                )}
              />
            </Flex>

            {createBy === 'XML' && (
              <Flex flexDirection="column" gap={2}>
                <Typography variant="h3">Appraisal XML</Typography>
                <Typography variant="body1">
                  Upload your appraisal XML to auto-populate your order with subject and
                  comparables. Toolkit currently accepts the 1004, 2055, 1004c and 1025 forms.
                </Typography>
                <Grid gridTemplateColumns="4fr" height="20%">
                  <FileUploader
                    maxFiles={1}
                    files={files}
                    onFileAdd={onFileUpload}
                    dropzoneMessage="Browse files or drag and drop your XML file."
                    onFileDelete={() => resetForm()}
                    accept={{ 'text/xml': ['.xml'] }}
                  />
                </Grid>

                <Link isExternal href={HELP_CENTER_URL}>
                  <Flex flexDirection="row" alignItems="center">
                    <Block paddingRight={2} paddingTop={1}>
                      <Icon icon="play-button" size="lg" />
                    </Block>
                    <Typography variant="link">
                      Need help? Find out how to get your appraisal XML in this video.
                    </Typography>
                  </Flex>
                </Link>
              </Flex>
            )}

            {isFormUnfurled && (
              <>
                <AddressInput addressLabel="Subject Property Address" />
                <Controller
                  name="propertyType"
                  control={form.control}
                  defaultValue="singleFamilyResidence"
                  render={({ field, fieldState }) => (
                    <Select
                      {...field}
                      isRequired
                      label="Property Type"
                      value={field.value}
                      options={CRAFTSMAN_PROPERTY_TYPE_OPTIONS}
                      onChange={(e) => {
                        if (isUndefined(e)) return form.resetField('propertyType');

                        return field.onChange(e);
                      }}
                      isInvalid={fieldState.invalid}
                    />
                  )}
                />
                <Flex gap="1rem" alignItems="flex-end">
                  <Controller
                    name="effectiveDate"
                    control={form.control}
                    defaultValue={AloftDate.today()}
                    render={({ field, fieldState }) => (
                      <Input
                        type="aloftDate"
                        label="Effective Date"
                        isRequired
                        {...field}
                        // @ts-expect-error TODO: no idea why this is angry
                        value={field.value}
                        onClear={() => form.resetField('effectiveDate')}
                        onChange={(e) => {
                          if (isUndefined(e) || e === '') return form.resetField('effectiveDate');

                          return field.onChange(e);
                        }}
                        isInvalid={fieldState.invalid}
                      />
                    )}
                  />
                  <Block width="20rem">
                    <Controller
                      name="orderNumber"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <SimpleInput
                          {...field}
                          type="text"
                          invalid={fieldState.invalid}
                          label={
                            <>
                              Order Number
                              <Typography variant="placeholder" inline>
                                &nbsp;(optional)
                              </Typography>
                            </>
                          }
                        />
                      )}
                    />
                  </Block>
                </Flex>
                {showInspectifyOrderModal && createBy !== 'XML' && (
                  <Flex>
                    <Controller
                      name="inspectifyOrder"
                      control={form.control}
                      render={({ field }) => (
                        <Checkbox
                          isChecked={field.value}
                          onChange={() => field.onChange(!field.value)}
                        >
                          <Typography>Inspectify order</Typography>
                        </Checkbox>
                      )}
                    />
                  </Flex>
                )}
                {inspectifyOrder && (
                  <>
                    <Flex gap={2}>
                      <Controller
                        name="clientName"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <Input
                            type="text"
                            label="Client Name"
                            {...field}
                            value={field.value}
                            onClear={() => form.resetField('clientName')}
                            onChange={(e) => {
                              if (isUndefined(e) || e === '') return form.resetField('clientName');

                              return field.onChange(e);
                            }}
                            isInvalid={fieldState.invalid}
                          />
                        )}
                      />
                      <Controller
                        name="dueDate"
                        control={form.control}
                        defaultValue={AloftDate.today().addDays(7)}
                        render={({ field, fieldState }) => (
                          <Input
                            type="aloftDate"
                            label="Due Date"
                            {...field}
                            // @ts-expect-error TODO: no idea why this is angry
                            value={field.value}
                            onClear={() => form.resetField('dueDate')}
                            onChange={(e) => {
                              if (isUndefined(e) || e === '') return form.resetField('dueDate');

                              return field.onChange(e);
                            }}
                            isInvalid={fieldState.invalid}
                          />
                        )}
                      />
                    </Flex>
                    <Flex gap={2}>
                      <Controller
                        name="borrowerName"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <Input
                            type="text"
                            label="Borrower Name"
                            {...field}
                            value={field.value}
                            onClear={() => form.resetField('borrowerName')}
                            onChange={(e) => {
                              if (isUndefined(e) || e === '')
                                return form.resetField('borrowerName');

                              return field.onChange(e);
                            }}
                            isInvalid={fieldState.invalid}
                          />
                        )}
                      />
                      <Controller
                        name="intendedUse"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <Input
                            type="text"
                            label="Intended Use"
                            {...field}
                            value={field.value}
                            onClear={() => form.resetField('intendedUse')}
                            onChange={(e) => {
                              if (isUndefined(e) || e === '') return form.resetField('intendedUse');

                              return field.onChange(e);
                            }}
                            isInvalid={fieldState.invalid}
                          />
                        )}
                      />
                    </Flex>
                    <Flex>
                      <Controller
                        name="appraiserInstructions"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <Input
                            type="multiline"
                            label="Appraiser Instructions"
                            resize="none"
                            {...field}
                            value={field.value}
                            onClear={() => form.resetField('appraiserInstructions')}
                            onChange={(e) => {
                              if (isUndefined(e) || e === '') {
                                return form.resetField('appraiserInstructions');
                              }

                              return field.onChange(e);
                            }}
                            isInvalid={fieldState.invalid}
                          />
                        )}
                      />
                    </Flex>
                    <Flex gap={2}>
                      <Controller
                        name="appraisalType"
                        control={form.control}
                        defaultValue="Hybrid Appraisal"
                        render={({ field, fieldState }) => (
                          <Select
                            {...field}
                            isRequired
                            label="Appraisal Type"
                            value={field.value}
                            options={APPRAISAL_TYPE_OPTIONS}
                            onChange={(e) => {
                              if (isUndefined(e)) return form.resetField('appraisalType');

                              return field.onChange(e);
                            }}
                            isInvalid={fieldState.invalid}
                          />
                        )}
                      />
                    </Flex>
                    {appraisalType === 'Hybrid Appraisal' && (
                      <Flex gap={2}>
                        <Controller
                          name="inspectionDate"
                          control={form.control}
                          defaultValue={AloftDate.today()}
                          render={({ field, fieldState }) => (
                            <Input
                              type="aloftDate"
                              label="Inspection Date"
                              isRequired
                              {...field}
                              // @ts-expect-error TODO: no idea why this is angry
                              value={field.value}
                              onClear={() => form.resetField('inspectionDate')}
                              onChange={(e) => {
                                if (isUndefined(e) || e === '') {
                                  return form.resetField('inspectionDate');
                                }

                                return field.onChange(e);
                              }}
                              isInvalid={fieldState.invalid}
                            />
                          )}
                        />
                        <Controller
                          name="inspectorType"
                          control={form.control}
                          defaultValue="Inspector"
                          render={({ field, fieldState }) => (
                            <Select
                              {...field}
                              isRequired
                              label="Inspector Type"
                              value={field.value}
                              options={INSPECTOR_TYPE_OPTIONS}
                              onChange={(e) => {
                                if (isUndefined(e)) return form.resetField('inspectorType');

                                return field.onChange(e);
                              }}
                              isInvalid={fieldState.invalid}
                            />
                          )}
                        />
                      </Flex>
                    )}

                    {/* Order options (Cost to Cure / ARV) */}
                    <Flex flexDirection="column" gap={2}>
                      <Typography variant="h3">Order Options</Typography>
                      <Flex gap={4}>
                        <Controller
                          name="options.costToCure"
                          control={form.control}
                          render={({ field }) => (
                            <Checkbox
                              isChecked={!!field.value}
                              onChange={() => field.onChange(!field.value)}
                            >
                              <Typography>Cost to Cure</Typography>
                            </Checkbox>
                          )}
                        />
                        <Controller
                          name="options.arv"
                          control={form.control}
                          render={({ field }) => (
                            <Checkbox
                              isChecked={!!field.value}
                              onChange={() => field.onChange(!field.value)}
                            >
                              <Typography>ARV</Typography>
                            </Checkbox>
                          )}
                        />
                      </Flex>
                    </Flex>
                  </>
                )}
                <AddressInputErrors />
                &nbsp;
              </>
            )}

            {isFormUnfurled && createBy === 'XML' && (
              <Flex flexDirection="row">
                <Block paddingRight={2} paddingTop={1}>
                  <Icon icon="information" size="lg" />
                </Block>
                <Typography>
                  Property data from your XML will be supplemented with listing and public records
                  data.
                </Typography>
              </Flex>
            )}
            {errors.propertyType?.message && (
              <Typography color="alertError" key="error-formtype">
                {errors.propertyType.message}
              </Typography>
            )}
          </Flex>
        </FormProvider>
      </Modal>
    </>
  );
};
